// API Service Layer for Backend Communication
import config from '../constants/environment.js';

const API_BASE_URL = config.api.baseUrl;
const API_TIMEOUT = config.api.timeout;
const ENABLE_LOGGING = config.features.enableLogging;
const MOCK_API = config.development.mockApi;

// Mock data for development
const mockData = {
  tickets: [
    {
      id: 'ticket-1',
      title: 'Implement user authentication',
      description: 'Add login and registration functionality',
      status: 'todo',
      priority: 'high',
      assignee: 'Sarah K.',
      reporter: 'Alex M.',
      created: '2025-01-15T10:00:00Z',
      updated: '2025-01-15T10:00:00Z',
      labels: ['frontend', 'security'],
      epic: 'Security Enhancement',
      storyPoints: 8,
      type: 'story'
    },
    {
      id: 'ticket-2',
      title: 'Fix login button styling',
      description: 'The login button is not properly styled on mobile devices',
      status: 'in-progress',
      priority: 'medium',
      assignee: 'Kevin L.',
      reporter: 'Yuki T.',
      created: '2025-01-14T14:30:00Z',
      updated: '2025-01-16T09:15:00Z',
      labels: ['frontend', 'bug'],
      epic: 'UI/UX Improvements',
      storyPoints: 3,
      type: 'bug'
    },
    {
      id: 'ticket-3',
      title: 'Add user profile page',
      description: 'Create a comprehensive user profile page with edit functionality',
      status: 'done',
      priority: 'medium',
      assignee: 'Yuki T.',
      reporter: 'Sarah K.',
      created: '2025-01-10T08:00:00Z',
      updated: '2025-01-16T16:45:00Z',
      labels: ['frontend', 'feature'],
      epic: 'User Management',
      storyPoints: 5,
      type: 'story'
    }
  ],
  projects: [
    {
      id: 'project-1',
      name: 'Task Management System',
      description: 'A comprehensive task management application',
      status: 'active',
      created: '2025-01-01T00:00:00Z',
      updated: '2025-01-16T00:00:00Z'
    }
  ]
};

// Helper function to simulate API delay
const simulateApiDelay = () => new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));

// Helper function to log API calls
const logApiCall = (method, url, data = null) => {
  if (ENABLE_LOGGING) {
    console.log(`[API] ${method} ${url}`, data ? { data } : '');
  }
};

// Helper function to log API responses
const logApiResponse = (method, url, response, error = null) => {
  if (ENABLE_LOGGING) {
    if (error) {
      console.error(`[API] ${method} ${url} - Error:`, error);
    } else {
      console.log(`[API] ${method} ${url} - Success:`, response);
    }
  }
};

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const fetchConfig = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    timeout: API_TIMEOUT,
    ...options,
  };

  // Add API key if authentication is enabled
  const apiKey = config.api.apiKey;
  if (apiKey) {
    fetchConfig.headers['X-API-Key'] = apiKey;
  }

  logApiCall(fetchConfig.method, url, options.body ? JSON.parse(options.body) : null);

  try {
    console.log('Making API request to:', url);
    console.log('Request body:', options.body);
    console.log('Request headers:', fetchConfig.headers);
    
    const response = await fetch(url, fetchConfig);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    logApiResponse(fetchConfig.method, url, data);
    return data;
  } catch (error) {
    logApiResponse(fetchConfig.method, url, null, error);
    throw error;
  }
};

// Tickets API
export const ticketsApi = {
  // Get all tickets
  getAll: async () => {
    if (MOCK_API) {
      await simulateApiDelay();
      logApiCall('GET', '/employee-service/task/tickets');
      return [...mockData.tickets];
    }
    return apiRequest('/employee-service/task/tickets');
  },
  
  // Create a new ticket
  create: async (ticketData) => {
    if (MOCK_API) {
      await simulateApiDelay();
      const newTicket = {
        id: `ticket-${Date.now()}`,
        ...ticketData,
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      };
      mockData.tickets.push(newTicket);
      logApiCall('POST', '/employee-service/task/tickets', ticketData);
      return newTicket;
    }
    return apiRequest('/employee-service/task/tickets', {
      method: 'POST',
      body: JSON.stringify(ticketData),
    });
  },
  
  // Update a ticket
  update: async (id, ticketData) => {
    if (MOCK_API) {
      await simulateApiDelay();
      const ticketIndex = mockData.tickets.findIndex(t => t.id === id);
      if (ticketIndex !== -1) {
        mockData.tickets[ticketIndex] = {
          ...mockData.tickets[ticketIndex],
          ...ticketData,
          updated: new Date().toISOString()
        };
        logApiCall('PUT', `/employee-service/task/tickets/${id}`, ticketData);
        return mockData.tickets[ticketIndex];
      }
      throw new Error('Ticket not found');
    }
    return apiRequest(`/employee-service/task/tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(ticketData),
    });
  },
  
  // Delete a ticket
  delete: async (id) => {
    if (MOCK_API) {
      await simulateApiDelay();
      const ticketIndex = mockData.tickets.findIndex(t => t.id === id);
      if (ticketIndex !== -1) {
        mockData.tickets.splice(ticketIndex, 1);
        logApiCall('DELETE', `/employee-service/task/tickets/${id}`);
        return { success: true };
      }
      throw new Error('Ticket not found');
    }
    return apiRequest(`/employee-service/task/tickets/${id}`, {
      method: 'DELETE',
    });
  },
};

// Projects API
export const projectsApi = {
  // Get all projects
  getAll: async () => {
    if (MOCK_API) {
      await simulateApiDelay();
      logApiCall('GET', '/employee-service/task/projects');
      return [...mockData.projects];
    }
    return apiRequest('/employee-service/task/projects');
  },
  
  // Create a new project
  create: async (projectData) => {
    if (MOCK_API) {
      await simulateApiDelay();
      const newProject = {
        id: `project-${Date.now()}`,
        ...projectData,
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      };
      mockData.projects.push(newProject);
      logApiCall('POST', '/employee-service/task/projects', projectData);
      return newProject;
    }
    return apiRequest('/employee-service/task/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  },
  
  // Update a project
  update: async (id, projectData) => {
    if (MOCK_API) {
      await simulateApiDelay();
      const projectIndex = mockData.projects.findIndex(p => p.id === id);
      if (projectIndex !== -1) {
        mockData.projects[projectIndex] = {
          ...mockData.projects[projectIndex],
          ...projectData,
          updated: new Date().toISOString()
        };
        logApiCall('PUT', `/employee-service/task/projects/${id}`, projectData);
        return mockData.projects[projectIndex];
      }
      throw new Error('Project not found');
    }
    return apiRequest(`/employee-service/task/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });
  },
  
  // Delete a project
  delete: async (id) => {
    if (MOCK_API) {
      await simulateApiDelay();
      const projectIndex = mockData.projects.findIndex(p => p.id === id);
      if (projectIndex !== -1) {
        mockData.projects.splice(projectIndex, 1);
        logApiCall('DELETE', `/employee-service/task/projects/${id}`);
        return { success: true };
      }
      throw new Error('Project not found');
    }
    return apiRequest(`/employee-service/task/projects/${id}`, {
      method: 'DELETE',
    });
  },
};

// System API
export const systemApi = {
  // Health check
  health: () => apiRequest('/health'),
  
  // Get logs
  getLogs: () => apiRequest('/employee-service/task/logs'),
};

// Export the base API configuration for debugging
export const apiConfig = {
  baseUrl: API_BASE_URL,
  timeout: API_TIMEOUT,
  enableLogging: ENABLE_LOGGING,
};

const apiService = {
  tickets: ticketsApi,
  projects: projectsApi,
  system: systemApi,
  config: apiConfig,
};

export default apiService;
